import React, { useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useFormik } from "formik";
import * as Yup from "yup";
import { hideModal } from "./modalHelper";
import {
    API, TYPES, STATUTS, PRIORITES, ETAPES_VENTE,
    findType, findStatut, findPriorite, findEtape, toDateInput, apiMessage, toast, unwrap,
} from "./projetsCommon";

export const PROJET_MODAL_ID = "projetFormModal";

const emptyValues = {
    code: "", nom: "", type: 4, priorite: 2, statut: 1,
    clientId: "", responsableId: "",
    dateDebut: "", dateFin: "", dateDebutReelle: "", dateFinReelle: "",
    avancement: 0, budget: "", description: "",
    lieu: "", adresse: "", ville: "",
    montantEstime: "", probabilite: "", etapeVente: "",
};

const after = (startField, message) => Yup.string().nullable().test("apres", message, function (v) {
    const start = this.parent[startField];
    return !v || !start || v >= start;
});
const optionalNumber = (label, min, max) => {
    let s = Yup.number().typeError(`${label} invalide`).nullable().transform((v, o) => (o === "" ? null : v)).min(min, `${label} invalide`);
    return max !== undefined ? s.max(max, `${label} : ${max} maximum`) : s;
};

const schema = Yup.object({
    nom: Yup.string().trim().required("Le nom est obligatoire").max(150, "150 caractères maximum"),
    code: Yup.string().nullable().max(30, "30 caractères maximum"),
    dateFin: after("dateDebut", "La fin prévue doit suivre le début"),
    dateFinReelle: after("dateDebutReelle", "La fin réelle doit suivre le début réel"),
    avancement: optionalNumber("Avancement", 0, 100),
    budget: optionalNumber("Budget", 0),
    montantEstime: optionalNumber("Montant", 0),
    probabilite: optionalNumber("Probabilité", 0, 100),
});

const toValues = (p) => ({
    code: p.code ?? "",
    nom: p.nom ?? "",
    type: findType(p.type).value,
    priorite: findPriorite(p.priorite).value,
    statut: findStatut(p.statut).value,
    clientId: p.clientId ?? "",
    responsableId: p.responsableId ?? "",
    dateDebut: toDateInput(p.dateDebut),
    dateFin: toDateInput(p.dateFin),
    dateDebutReelle: toDateInput(p.dateDebutReelle),
    dateFinReelle: toDateInput(p.dateFinReelle),
    avancement: p.avancement ?? 0,
    budget: p.budget ?? "",
    description: p.description ?? "",
    lieu: p.lieu ?? "",
    adresse: p.adresse ?? "",
    ville: p.ville ?? "",
    montantEstime: p.montantEstime ?? "",
    probabilite: p.probabilite ?? "",
    etapeVente: findEtape(p.etapeVente)?.value ?? "",
});

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const txt = (v) => (v && String(v).trim() ? String(v).trim() : null);

/**
 * Formulaire création / modification de projet (popup).
 * projet = null → création ; openKey : à incrémenter à chaque ouverture (réinitialise le formulaire).
 */
const ProjetFormModal = ({ projet, openKey, clients, employes, onSaved }) => {
    const formik = useFormik({
        initialValues: emptyValues,
        validationSchema: schema,
        onSubmit: async (v, { setErrors }) => {
            const payload = {
                code: txt(v.code), nom: v.nom.trim(),
                type: Number(v.type), priorite: Number(v.priorite), statut: Number(v.statut),
                clientId: num(v.clientId), responsableId: num(v.responsableId),
                dateDebut: v.dateDebut || null, dateFin: v.dateFin || null,
                dateDebutReelle: v.dateDebutReelle || null, dateFinReelle: v.dateFinReelle || null,
                avancement: Number(v.avancement) || 0,
                budget: num(v.budget), description: txt(v.description),
                lieu: txt(v.lieu), adresse: txt(v.adresse), ville: txt(v.ville),
                montantEstime: num(v.montantEstime), probabilite: num(v.probabilite), etapeVente: num(v.etapeVente),
                coverDocumentId: projet?.coverDocumentId ?? null,
            };
            try {
                const res = projet
                    ? await axiosInstance.put(API.projet(projet.id), payload)
                    : await axiosInstance.post(API.projets, payload);
                hideModal(PROJET_MODAL_ID);
                toast("success", projet ? "Projet modifié" : "Projet créé");
                onSaved && onSaved(unwrap(res));
            } catch (error) {
                if (error.response?.data?.errors) setErrors(error.response.data.errors);
                else toast("error", apiMessage(error, "Enregistrement échoué"));
            }
        },
    });

    // Recharge le formulaire à chaque ouverture (création ou autre projet)
    useEffect(() => {
        formik.resetForm({ values: projet ? toValues(projet) : emptyValues });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projet, openKey]);

    const f = formik;
    const type = Number(f.values.type);
    const invalid = (name) => (f.touched[name] && f.errors[name] ? "is-invalid" : "");
    const input = (name, label, props = {}) => (
        <>
            <label>{label}</label>
            <input name={name} className={`form-control ${invalid(name)}`} value={f.values[name] ?? ""}
                onChange={f.handleChange} onBlur={f.handleBlur} {...props} />
            <div className="invalid-feedback">{f.errors[name]}</div>
        </>
    );
    const select = (name, label, options, emptyLabel) => (
        <>
            <label>{label}</label>
            <select name={name} className={`form-control ${invalid(name)}`} value={f.values[name] ?? ""}
                onChange={f.handleChange} onBlur={f.handleBlur}>
                {emptyLabel !== undefined && <option value="">{emptyLabel}</option>}
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <div className="invalid-feedback">{f.errors[name]}</div>
        </>
    );

    return (
        <div className="modal fade" id={PROJET_MODAL_ID}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5><i className="fas fa-project-diagram mr-2"></i>{projet ? "Modifier le projet" : "Nouveau projet"}</h5>
                        <button type="button" className="close" data-dismiss="modal"><span>&times;</span></button>
                    </div>
                    <div className="modal-body">
                        {/* Type : choix en boutons, il conditionne les champs */}
                        <div className="btn-group btn-group-toggle d-flex mb-3">
                            {TYPES.map((t) => (
                                <button key={t.value} type="button"
                                    className={`btn btn-sm ${type === t.value ? "btn-primary" : "btn-outline-primary"} flex-fill`}
                                    onClick={() => f.setFieldValue("type", t.value)}>
                                    <i className={`fas ${t.icon} mr-1`}></i>{t.label}
                                </button>
                            ))}
                        </div>

                        <div className="form-row">
                            <div className="form-group col-md-3">{input("code", "Code", { placeholder: "ex : CH-2026-01" })}</div>
                            <div className="form-group col-md-9">{input("nom", "Nom *")}</div>
                        </div>
                        <div className="form-row">
                            <div className="form-group col-md-4">
                                {select("clientId", "Client", clients.map((c) => ({ value: c.id, label: c.name })), "— Aucun —")}
                            </div>
                            <div className="form-group col-md-4">
                                {select("responsableId", "Responsable", employes.map((e) => ({ value: e.id, label: e.nomComplet })), "— Aucun —")}
                            </div>
                            <div className="form-group col-md-2">{select("statut", "Statut", STATUTS)}</div>
                            <div className="form-group col-md-2">{select("priorite", "Priorité", PRIORITES)}</div>
                        </div>

                        {type === 1 && (
                            <fieldset className="gp-fieldset">
                                <legend><i className="fas fa-hard-hat mr-1"></i>Chantier</legend>
                                <div className="form-row">
                                    <div className="form-group col-md-4">{input("lieu", "Libellé du lieu", { placeholder: "ex : Villa Souissi" })}</div>
                                    <div className="form-group col-md-5">{input("adresse", "Adresse")}</div>
                                    <div className="form-group col-md-3">{input("ville", "Ville")}</div>
                                </div>
                            </fieldset>
                        )}
                        {type === 2 && (
                            <fieldset className="gp-fieldset">
                                <legend><i className="fas fa-handshake mr-1"></i>Vente</legend>
                                <div className="form-row">
                                    <div className="form-group col-md-4">{select("etapeVente", "Étape", ETAPES_VENTE, "— Choisir —")}</div>
                                    <div className="form-group col-md-4">{input("montantEstime", "Montant estimé (DH)", { type: "number", step: "0.01", min: 0 })}</div>
                                    <div className="form-group col-md-4">{input("probabilite", "Probabilité (%)", { type: "number", min: 0, max: 100 })}</div>
                                </div>
                            </fieldset>
                        )}
                        {type !== 1 && (
                            <div className="form-row">
                                <div className="form-group col-md-6">{input("lieu", "Lieu")}</div>
                            </div>
                        )}

                        <fieldset className="gp-fieldset">
                            <legend><i className="far fa-calendar-alt mr-1"></i>Planning</legend>
                            <div className="form-row">
                                <div className="form-group col-md-3">{input("dateDebut", "Début prévu", { type: "date" })}</div>
                                <div className="form-group col-md-3">{input("dateFin", "Fin prévue", { type: "date" })}</div>
                                <div className="form-group col-md-3">{input("dateDebutReelle", "Début réel", { type: "date" })}</div>
                                <div className="form-group col-md-3">{input("dateFinReelle", "Fin réelle", { type: "date" })}</div>
                            </div>
                            <div className="form-row">
                                <div className="form-group col-md-3">{input("budget", "Budget (DH)", { type: "number", step: "0.01", min: 0 })}</div>
                                <div className="form-group col-md-9">
                                    <label>Avancement : <strong>{f.values.avancement || 0} %</strong></label>
                                    <input type="range" name="avancement" className="custom-range" min="0" max="100" step="5"
                                        value={f.values.avancement || 0} onChange={f.handleChange} />
                                </div>
                            </div>
                        </fieldset>

                        <div className="form-group mb-0">
                            <label>Description</label>
                            <textarea name="description" rows={3} className="form-control"
                                value={f.values.description} onChange={f.handleChange} />
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" data-dismiss="modal">Annuler</button>
                        <button type="button" className="btn btn-enregistrer" onClick={f.handleSubmit} disabled={f.isSubmitting}>
                            Sauvegarder
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjetFormModal;
